import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Title } from '@angular/platform-browser';
import * as XLSX from 'xlsx';
import { ProductService } from 'src/app/service/product.service';
import { ToastrNotificationService } from 'src/app/service/toastr-notification.service';
import { CreateMasterBatchDialogComponent } from './create-master-batch-dialog.component';
import { DeleteDialogBoxComponent } from '../delete-dialog-box/delete-dialog-box.component';

@Component({
  selector: 'app-master-batch',
  templateUrl: './master-batch.component.html',
  styleUrls: ['./master-batch.component.scss']
})
export class MasterBatchComponent implements OnInit, OnDestroy {
  displayedColumns: string[] = [
    'batchNumber',
    'range',
    'totalCount',
    'usedCount',
    'remaining',
    'createdAt',
    'actions'
  ];
  dataSource = new MatTableDataSource<any>([]);
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  spinner: boolean = true;
  exportingBatchId: string | null = null;
  private autoRefreshTimer?: any;

  constructor(
    private productService: ProductService,
    private titleService: Title,
    private toastr: ToastrNotificationService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.titleService.setTitle('Master Batches');
    this.loadMasterBatches();
  }

  ngOnDestroy(): void {
    if (this.autoRefreshTimer) {
      clearTimeout(this.autoRefreshTimer);
    }
  }

  loadMasterBatches(silent: boolean = false): void {
    if (!silent) {
      this.spinner = true;
    }
    this.productService.getAllMasterBatches().subscribe({
      next: (res) => {
        this.spinner = false;
        if (res.success) {
          const batches = (res.batches || []).sort((a: any, b: any) => {
            const timeA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
            const timeB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
            return timeB - timeA;
          });
          this.dataSource = new MatTableDataSource(batches);
          this.dataSource.paginator = this.paginator;
          this.dataSource.sort = this.sort;

          const hasGenerating = batches.some((b: any) => b.status === 'generating');
          if (hasGenerating) {
            if (this.autoRefreshTimer) {
              clearTimeout(this.autoRefreshTimer);
            }
            this.autoRefreshTimer = setTimeout(() => {
              this.loadMasterBatches(true);
            }, 3000);
          }
        } else {
          this.toastr.error(res.error || 'Failed to load master batches', 'Error');
        }
      },
      error: (err) => {
        this.spinner = false;
        this.toastr.error(err.message || 'Error loading master batches', 'Error');
      }
    });
  }

  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  openCreateBatchDialog(): void {
    const dialogRef = this.dialog.open(CreateMasterBatchDialogComponent, {
      width: '500px',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe((created) => {
      if (created) {
        this.loadMasterBatches();
      }
    });
  }


  deleteBatch(batch: any): void {
    if (batch.usedCount > 0) {
      this.toastr.warning('Cannot delete this batch because one or more codes have already been used.', 'Warning');
      return;
    }

    const dialogRef = this.dialog.open(DeleteDialogBoxComponent, {
      width: '350px'
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.productService.deleteMasterBatch(batch._id).subscribe({
          next: (res) => {
            if (res.success) {
              this.toastr.success('Master batch deleted successfully', 'Success');
              this.loadMasterBatches();
            } else {
              this.toastr.error(res.error || 'Failed to delete master batch', 'Error');
            }
          },
          error: (err) => {
            this.toastr.error(err.error?.error || err.message || 'Error deleting master batch', 'Error');
          }
        });
      }
    });
  }

  exportExcel(batch: any): void {
    this.exportingBatchId = batch._id;
    this.productService.getMasterBatchDetail(batch._id).subscribe({
      next: (res) => {
        this.exportingBatchId = null;
        if (res.success && res.batch?.items) {
          const items = (res.batch.items || []).slice().sort((a: any, b: any) => (a.seqNo || 0) - (b.seqNo || 0));
          const rows: any[] = [];
          for (let i = 0; i < items.length; i += 2) {
            const first = items[i];
            const second = items[i + 1];
            rows.push({
              'Barcode_1': first?.barcode || '',
              'QrCode_1': first?.qrCode || '',
              'Barcode_2': second?.barcode || '',
              'QrCode_2': second?.qrCode || ''
            });
          }

          const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(rows);
          ws['!cols'] = [
            { wch: 20 },
            { wch: 36 },
            { wch: 20 },
            { wch: 36 }
          ];

          const wb: XLSX.WorkBook = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(wb, ws, 'Codes');

          const fileName = `${batch.batchNumber || 'MasterBatch'}.xlsx`;
          XLSX.writeFile(wb, fileName);

          this.toastr.success(`Exported ${items.length} codes to Excel (${rows.length} rows)`, 'Success');
        } else {
          this.toastr.error('Failed to load batch codes for export', 'Error');
        }
      },
      error: (err) => {
        this.exportingBatchId = null;
        this.toastr.error(err.message || 'Error exporting batch to Excel', 'Error');
      }
    });
  }
}
